import { classColor, CLASSES } from "@eldrun/content";
import { MOVE } from "@eldrun/sim";
import { Box3, BufferGeometry, CylinderGeometry, EdgesGeometry, LineBasicMaterial, LineSegments, Mesh, PerspectiveCamera, PlaneGeometry, Scene, Vector3, type SkinnedMesh, type WebGLRenderer } from "three";
import { AVATAR_CLIPS } from "../assets/avatars.generated.ts";
import { FrameTimer } from "../engine/frame-loop.ts";
import { FrameStats } from "../engine/frame-stats.ts";
import { StatsHud } from "../engine/stats-hud.ts";
import type { AvatarClipName } from "../game/avatar-state.ts";
import { heldMesh } from "../game/held-item.ts";
import { SkinnedAvatar } from "../game/skinned-avatar.ts";
import { applyAtmosphere, createAtmosphereUniforms, sampleAtmosphere } from "../render/atmosphere.ts";
import { PostFx } from "../render/post/post-fx.ts";
import { countPrograms } from "../render/post/post-model.ts";
import type { AvatarLibrary, AvatarModelId } from "../render/avatar-library.ts";
import type { ModelLibrary } from "../render/model-library.ts";
import { createFoliageMaterial } from "../render/shaders/foliage.ts";
import { toonMaterial } from "../render/shaders/toon.ts";
import type { CharacterSurfaceStyle } from "../render/character-surface/config.ts";
import { scriptItem, scriptSignals, scriptZ, type AvatarScript } from "./avatar-script.ts";
import { createSurfacePanel } from "./surface-panel.ts";
import { Turnaround } from "./turnaround.ts";

export interface AvatarViewer {
  stats(): Record<string, unknown>;
  /** Desenha um quadro e devolve o canvas em PNG (F120; com alfa no fundo transparente). */
  snapshot(): string;
  stop(): void;
}

export interface AvatarViewerOptions {
  /** Um personagem (`cavaleiro`…) ou todos, lado a lado. */
  character: AvatarModelId | undefined;
  /** Clipe tocado no corpo inteiro. */
  clip: AvatarClipName;
  /** Instante fixo do clipe (s); sem ele, o clipe repete. */
  time: number | undefined;
  /** Item na mão de todos (`""` sem item). */
  item: string;
  /**
   * `frente`, `costas`, `lado` (de perfil, a mão direita para a câmera), `pes` (de perfil, rente ao
   * chão, perto dos pés), `direita`/`esquerda` (o lado do personagem, F120) ou `quatro` (as quatro
   * vistas ortográficas com o mesmo pivô, F120).
   */
  view: "frente" | "costas" | "lado" | "pes" | "direita" | "esquerda" | "quatro";
  /** Ajustes da superfície dos atores por cima dos do papel (F120; `surface-params.ts`). */
  surface?: Partial<CharacterSurfaceStyle>;
  /** Fundo transparente (F120): sem céu nem chão, alfa 0, sem o pós (o alfa do canvas é a máscara do contorno). */
  transparent?: boolean;
  /** Painel de ajuste da superfície (F120). */
  panel?: boolean;
  /** Contorno da cápsula do jogador (0,35 m de raio, 1,8 m) em volta de cada um, para comparar a escala. */
  capsule: boolean;
  /** Olhar para cima (+) ou para baixo (−), em rad: a mira no tronco e na cabeça. */
  pitch: number;
  phase: number;
  /**
   * Roteiro (`&andar=&agachado=1&gestos=`): os bonecos andam pela máquina de estados do jogo, a 60 Hz
   * fixos; com `time`, o roteiro para nesse instante (refeito do zero, então o quadro é sempre o mesmo).
   */
  script?: AvatarScript | undefined;
}

const SPACING = 1.6;
/** Passo fixo do roteiro (s): o mesmo instante dá sempre a mesma pose. */
const STEP = 1 / 60;
/** Ponta do pé apoiada: a menos disto (m) do ponto mais baixo dela no roteiro (o pé no ar passa mais alto). */
const CONTACT = 0.01;
/** O escorregão é medido depois disto (s): a entrada no estado do roteiro (de pé para agachado) fica de fora. */
const WARMUP = 0.5;

/** Mediana de uma lista (0 vazia). */
const median = (values: number[]) => (values.length === 0 ? 0 : [...values].sort((a, b) => a - b)[values.length >> 1]!);

/**
 * Vitrine dos avatares animados (`?debug=avatares&personagem=&clipe=&tempo=&item=&vista=frente|costas|lado&capsula=1&mira=`,
 * F49, sem servidor): os 6 personagens KayKit, cada um na cor do jogador dele, tocando um clipe do
 * contrato no corpo inteiro, parado num instante (`tempo`) ou em laço, com o item na mão no encaixe e,
 * se pedido, a cápsula do jogador em volta. Para conferir escala, pesos, pés e o item na mão sem abrir
 * uma partida. Com um roteiro (`&andar=&agachado=1&gestos=`, `avatar-script.ts`), os bonecos andam pela
 * máquina de estados do jogo sobre traços a cada 25 cm, e o `stats()` traz a pose de cada metade e o
 * escorregão medido da ponta do pé apoiada (`slip`, m/s no mundo). F120: `vista=direita|esquerda|quatro`
 * (as quatro ortográficas com o mesmo pivô), `superficie=0` e os ajustes da superfície
 * (`surface-params.ts`), `fundo=transparente` (alfa 0, para exportar PNG pela sonda `snapshot`) e
 * `painel=1` (o painel de ajuste).
 */
export function startAvatarViewer(renderer: WebGLRenderer, library: AvatarLibrary, models: ModelLibrary, options: AvatarViewerOptions): AvatarViewer {
  const scene = new Scene();
  const atmosphere = createAtmosphereUniforms();
  const sample = sampleAtmosphere(options.phase);
  scene.background = options.transparent ? null : sample.horizon;
  const clearAlpha = renderer.getClearAlpha();
  if (options.transparent) renderer.setClearAlpha(0);
  const camera = new PerspectiveCamera(50, 1, 0.1, 200);
  scene.add(camera);
  const ground = new Mesh(new PlaneGeometry(60, 60), toonMaterial(atmosphere, "#7FA34E", 0));
  ground.rotation.x = -Math.PI / 2;
  ground.visible = !options.transparent;
  scene.add(ground);
  const foliage = createFoliageMaterial(atmosphere, { outline: true });
  // As quatro vistas mostram um personagem (o pedido, ou o primeiro): a fila inteira encolheria cada um e se taparia de perfil.
  const ids = (options.character ? [options.character] : options.view === "quatro" ? library.models.slice(0, 1) : library.models).filter((id) => library.has(id));
  const lineup = ids.map((id, i) => {
    // Cor da classe que usa o personagem (F85); os de fora das classes (ladinos) ficam na padrão.
    const avatar = new SkinnedAvatar(library, atmosphere, id, options.surface?.accentColor ?? classColor(CLASSES.find((c) => c.character === id)?.id ?? ""), i, options.surface);
    avatar.root.position.set((i - (ids.length - 1) / 2) * SPACING, 0, 0);
    scene.add(avatar.root);
    const mesh = options.item ? heldMesh(options.item, models, foliage, "kaykit") : undefined;
    avatar.setHeld(mesh, options.item);
    if (options.capsule) {
      const capsule = new LineSegments(new EdgesGeometry(new CylinderGeometry(MOVE.radius, MOVE.radius, MOVE.height, 12, 1)), new LineBasicMaterial({ color: "#ffffff" }));
      capsule.position.set(avatar.root.position.x, MOVE.height / 2, 0);
      scene.add(capsule);
    }
    return avatar;
  });
  const width = Math.max(1, ids.length) * SPACING;
  const distance = Math.max(3.2, width * 0.95);
  const script = options.script;
  const walking = script !== undefined && script.speed > 0;
  // A direita do personagem é +x (ele olha para −z); "lado" é a direita, como antes.
  const side = options.view === "lado" || options.view === "direita" ? 1 : options.view === "esquerda" ? -1 : 0;
  // Os bonecos olham para −z: de frente, a câmera fica em −z; de costas, em +z; de lado, à direita (+x) ou à esquerda (−x) deles.
  // Andando, de perfil e mais baixa, com uma grade de 25 cm no chão para ver os pés parados nela.
  const far = walking ? Math.max(4.5, distance) : distance;
  if (options.view === "pes") {
    camera.position.set(2.2, 0.35, 0);
    camera.lookAt(new Vector3(0, 0.2, 0));
  } else {
    camera.position.set(side * far, walking ? 1 : 1.3, side ? 0 : options.view === "costas" ? far : -far);
    camera.lookAt(new Vector3(0, walking ? 0.6 : 0.95, 0));
  }
  if (walking) {
    // Traços no chão a cada 25 cm ao longo da pista (um pé apoiado fica parado sobre um deles).
    const points: Vector3[] = [];
    for (const avatar of lineup) {
      const x = avatar.root.position.x;
      for (let z = -6; z <= 6 + 1e-9; z += 0.25) points.push(new Vector3(x - 0.4, 0.003, z), new Vector3(x + 0.4, 0.003, z));
    }
    const marks = new LineSegments(new BufferGeometry().setFromPoints(points), new LineBasicMaterial({ color: "#2F4A1C" }));
    scene.add(marks);
  }
  const timer = new FrameTimer();
  const hud = new StatsHud(document.body);
  // F52: a contagem do quadro e o pós-processamento (`&pos=`; padrão, o Médio).
  const counter = new FrameStats(renderer.info);
  const unwatch = counter.watch(scene);
  const post = new PostFx(renderer);
  post.bind(atmosphere);
  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize);
  resize();

  // Roteiro: o instante, os passos dados, o item de cada um e as pontas dos pés a cada passo (para o
  // escorregão medido no mundo: a ponta apoiada de quem anda sem escorregar fica parada no chão).
  let scriptTime = 0;
  let steps = 0;
  let pending = 0;
  const heldNow = lineup.map(() => options.item);
  const feet = lineup.map(() => [[], []] as { t: number; x: number; y: number; z: number }[][]);
  const toes = lineup.map((avatar) => ["toesl", "toesr"].map((name) => avatar.root.getObjectByName(name)));
  const at = new Vector3();
  const stepScript = (s: AvatarScript) => {
    // O passo leva o roteiro ao fim dele: a pose mostrada é a do instante `scriptTime`.
    steps++;
    scriptTime = steps * STEP;
    const signals = scriptSignals(s, scriptTime);
    lineup.forEach((avatar, i) => {
      const item = scriptItem(s, scriptTime, options.item);
      if (item !== heldNow[i]) {
        heldNow[i] = item;
        avatar.setHeld(item ? heldMesh(item, models, foliage, "kaykit") : undefined, item);
      }
      avatar.root.position.z = scriptZ(s, scriptTime);
      avatar.update({ dt: STEP, now: scriptTime * 1000, yaw: 0, pitch: options.pitch, signals, armSwing: signals.swing, diedAt: -1, distance: 5, visible: true, frame: steps, range: 1 });
      avatar.root.updateMatrixWorld(true);
      toes[i]!.forEach((toe, f) => {
        if (!toe) return;
        toe.getWorldPosition(at);
        const track = feet[i]![f]!;
        // A volta do roteiro teleporta o boneco: o rastro recomeça.
        if (track.length > 0 && scriptZ(s, scriptTime) > scriptZ(s, track.at(-1)!.t) + 1e-6) track.length = 0;
        track.push({ t: scriptTime, x: at.x, y: at.y, z: at.z });
        if (track.length > 600) track.shift();
      });
    });
  };
  if (script && options.time !== undefined) while (steps < Math.round(options.time / STEP)) stepScript(script);
  // As quatro vistas (F120): a pose mostrada (depois do roteiro, se houver) enquadra todas com o mesmo
  // pivô; andando, o pivô segue o boneco a cada quadro com a mesma escala.
  const turnaround = options.view === "quatro" ? new Turnaround() : undefined;
  if (turnaround) {
    if (!script) for (const avatar of lineup) avatar.showClip(options.clip, Math.min(options.time ?? 0, AVATAR_CLIPS[options.clip].duration - 1e-3), options.pitch);
    else if (steps === 0) stepScript(script);
    turnaround.frame(lineup.map((a) => a.root));
  }
  // Sem pós no fundo transparente e nas quatro vistas (o pós ocupa o canvas inteiro e escreve o alfa).
  const direct = options.transparent === true || turnaround !== undefined;
  const panel = options.panel && lineup[0] ? createSurfacePanel(document.body, lineup[0].surfaceHandle.style, (change) => lineup.forEach((a) => a.surfaceHandle.set(change))) : undefined;

  /**
   * Mediana da velocidade no chão (m/s, em x e z, no mundo) da ponta do pé apoiada no último segundo e
   * meio (a entrada no roteiro fica de fora): de quem anda sem escorregar, perto de 0.
   */
  const slip = (i: number) =>
    median(
      feet[i]!.flatMap((all) => {
        const track = all.filter((p) => p.t >= Math.max(WARMUP, scriptTime - 1.5));
        const low = Math.min(...track.map((p) => p.y));
        return track.slice(1).flatMap((b, k) => {
          const a = track[k]!;
          return a.y < low + CONTACT && b.y < low + CONTACT ? [Math.hypot(b.x - a.x, b.z - a.z) / (b.t - a.t)] : [];
        });
      }),
    );

  const draw = (dt: number) => {
    if (turnaround) {
      if (script) turnaround.frame(lineup.map((a) => a.root), true);
      turnaround.render(renderer, scene);
    }
    else if (direct) renderer.render(scene, camera);
    else post.render(scene, camera, dt, counter);
  };

  const duration = AVATAR_CLIPS[options.clip].duration;
  let clipTime = options.time ?? 0;
  let last = performance.now();
  renderer.setAnimationLoop((now: number) => {
    counter.begin();
    const dt = Math.min(0.1, (now - last) / 1000);
    timer.push(now - last);
    last = now;
    if (script) {
      if (options.time === undefined) {
        pending += dt;
        while (pending >= STEP) {
          pending -= STEP;
          stepScript(script);
        }
      }
    } else {
      if (options.time === undefined) clipTime = (clipTime + dt) % duration;
      for (const avatar of lineup) avatar.showClip(options.clip, Math.min(clipTime, duration - 1e-3), options.pitch);
    }
    applyAtmosphere(atmosphere, sample, options.time === undefined ? now / 1000 : 0);
    draw(dt);
    const f = counter.end();
    const playing = script ? `roteiro @ ${scriptTime.toFixed(2)} s · ${script.speed} m/s${script.crouch ? " agachado" : ""}` : `${options.clip} @ ${clipTime.toFixed(2)} s de ${duration.toFixed(2)} s`;
    hud.update(now, () => [`Eldrun · avatares · ${timer.fps.toFixed(0)} fps · draw calls ${f.drawCalls} (+${f.postCalls} do pós) · programas ${countPrograms(renderer.info.programs).scene}`, playing]);
  });

  return {
    stats: () => {
      const characters = lineup.map((avatar, i) => {
        let mesh: SkinnedMesh | undefined;
        avatar.root.traverse((o) => (o as SkinnedMesh).isSkinnedMesh && (mesh = o as SkinnedMesh));
        const box = mesh ? new Box3().setFromObject(mesh, true) : new Box3();
        const pose = avatar.pose;
        // Lateralidade (F120): o encaixe da mão direita no espaço do boneco (sem o lugar dele na fila); a direita do personagem é +x.
        const handWorld = avatar.root.getObjectByName("handslotr")?.getWorldPosition(new Vector3());
        const hand = handWorld && avatar.root.worldToLocal(handWorld);
        return {
          ...avatar.stats(),
          rightHandX: hand ? +hand.x.toFixed(3) : undefined,
          height: +(box.max.y - box.min.y).toFixed(3),
          minY: +box.min.y.toFixed(3),
          tris: (mesh?.geometry.index?.count ?? 0) / 3,
          ...(script && { state: pose?.state, lower: pose?.lower, upper: pose?.upper, slip: +slip(i).toFixed(3), z: +avatar.root.position.z.toFixed(3) }),
        };
      });
      return {
        fps: timer.fps,
        drawCalls: counter.last.drawCalls,
        frameCalls: counter.last.frameCalls,
        // Programas da cena (F52: os do pós à parte, em `post.programs`).
        programs: countPrograms(renderer.info.programs).scene,
        post: post.stats(counter.last),
        clip: options.clip,
        time: script ? scriptTime : clipTime,
        ...(script && { script: { speed: script.speed, crouch: script.crouch, steps } }),
        characters,
        held: options.item,
        view: options.view,
        transparent: options.transparent === true,
        surface: lineup[0]?.surfaceHandle.style,
        ...(turnaround && { turnaround: { center: turnaround.center.toArray().map((v) => +v.toFixed(3)), half: +turnaround.half.toFixed(3), extent: turnaround.extent.toArray().map((v) => +v.toFixed(3)) } }),
      };
    },
    snapshot: () => {
      // Lido logo depois do desenho, na mesma tarefa: sem `preserveDrawingBuffer`.
      applyAtmosphere(atmosphere, sample, 0);
      draw(0);
      return renderer.domElement.toDataURL("image/png");
    },
    stop() {
      renderer.setAnimationLoop(null);
      post.dispose();
      unwatch();
      window.removeEventListener("resize", resize);
      for (const avatar of lineup) avatar.dispose();
      hud.remove();
      panel?.remove();
      renderer.setClearAlpha(clearAlpha);
    },
  };
}
